import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { ActivationHero } from '@/components/auth/activation-hero';
import { AuthShell } from '@/components/auth/auth-shell';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  AGE_ATTESTATION_VERSION,
  PRIVACY_POLICY_VERSION,
  TERMS_OF_SERVICE_VERSION,
} from '@/constants/legal';
import { resetPassword } from '@/services/api.client';

// AUTH-05 — Account Activation.
// The server treats activation as a password-reset with a single-use token
// issued by the activation email (api-design.md §10, user routes).
//
// KDPA §32 (consent) + §33 (children's data) are now captured here:
//   * privacyPolicyAccepted + termsAccepted — explicit checkboxes; required
//   * ageEighteenPlus — explicit attestation; required
// All three booleans must be true to enable the submit button. The server
// also enforces the same constraint (auth.service.ts) so a tampered client
// cannot bypass it.

const STRONG_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;
const PASSWORD_RULES = 'At least 8 characters with uppercase, lowercase, and a digit';

const schema = z
  .object({
    newPassword: z.string().min(8, PASSWORD_RULES).regex(STRONG_PASSWORD_REGEX, PASSWORD_RULES),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    privacyPolicyAccepted: z.literal(true, { error: 'You must accept the Privacy Policy' }),
    termsAccepted: z.literal(true, { error: 'You must accept the Terms of Service' }),
    ageEighteenPlus: z.literal(true, {
      error: 'You must confirm you are at least 18 years old',
    }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

export function ActivationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      privacyPolicyAccepted: false as unknown as true,
      termsAccepted: false as unknown as true,
      ageEighteenPlus: false as unknown as true,
    },
  });

  const privacyAccepted = watch('privacyPolicyAccepted');
  const termsAcceptedValue = watch('termsAccepted');
  const ageOk = watch('ageEighteenPlus');

  const mutation = useMutation({
    mutationFn: ({ newPassword }: FormValues) => {
      if (!token) throw new Error('Activation token is missing from the URL');
      return resetPassword(token, newPassword, {
        privacyPolicyAccepted: true,
        privacyPolicyVersion: PRIVACY_POLICY_VERSION,
        termsAccepted: true,
        termsVersion: TERMS_OF_SERVICE_VERSION,
        ageEighteenPlus: true,
        ageAttestationVersion: AGE_ATTESTATION_VERSION,
      });
    },
    onSuccess: () => {
      void navigate('/login', { state: { accountActivated: true } });
    },
  });

  const onSubmit = (values: FormValues) => {
    mutation.mutate(values);
  };

  if (!token) {
    return (
      <AuthShell
        hero={<ActivationHero />}
        title="Invalid Activation Link"
        description="This activation link is missing required information. Please contact your property manager for a new invitation."
      >
        <Link
          to="/login"
          className="flex w-full justify-center text-sm underline underline-offset-4 hover:text-[var(--text-accent)]"
        >
          Back to sign in
        </Link>
      </AuthShell>
    );
  }

  const isTokenExpired =
    mutation.isError &&
    mutation.error instanceof Object &&
    'status' in mutation.error &&
    (mutation.error as { status: number }).status === 401;

  if (isTokenExpired) {
    return (
      <AuthShell
        hero={<ActivationHero />}
        title="Activation Link Expired"
        description="This activation link has expired or has already been used. Please contact your property manager for a new invitation."
      >
        <Button asChild variant="outline">
          <Link to="/login">Back to sign in</Link>
        </Button>
      </AuthShell>
    );
  }

  const apiError =
    mutation.isError && mutation.error instanceof Object && 'message' in mutation.error
      ? String((mutation.error as { message: string }).message)
      : null;

  return (
    <AuthShell
      hero={<ActivationHero />}
      title="Create Your Account"
      description="Activate your account by setting a strong password."
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="newPassword">Create Password</FieldLabel>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                disabled={mutation.isPending}
                aria-invalid={!!errors.newPassword}
                {...register('newPassword')}
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-foreground hover:text-foreground"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
              </button>
            </div>
            {errors.newPassword ? (
              <p className="text-sm text-destructive" role="alert">
                {errors.newPassword.message}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">{PASSWORD_RULES}</p>
            )}
          </Field>
          <Field>
            <FieldLabel htmlFor="confirmPassword">Confirm Password</FieldLabel>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                disabled={mutation.isPending}
                aria-invalid={!!errors.confirmPassword}
                {...register('confirmPassword')}
              />
              <button
                type="button"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-foreground hover:text-foreground"
                onClick={() => setShowConfirm((v) => !v)}
              >
                {showConfirm ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-destructive" role="alert">
                {errors.confirmPassword.message}
              </p>
            )}
          </Field>
          {/* KDPA §32 + §33 — consent + age-of-majority gate. Required to enable submit. */}
          <Field>
            <FieldLabel className="text-sm font-medium">Privacy &amp; consent</FieldLabel>
            <div className="space-y-3 rounded-md border border-[var(--border-default)] bg-[var(--bg-surface)] p-3">
              <label className="flex items-start gap-3 text-sm">
                <Checkbox
                  id="ageEighteenPlus"
                  checked={!!ageOk}
                  onCheckedChange={(v) =>
                    setValue(
                      'ageEighteenPlus',
                      v === true ? (true as true) : (false as unknown as true),
                      { shouldValidate: true },
                    )
                  }
                  aria-invalid={!!errors.ageEighteenPlus}
                />
                <span>
                  I confirm that I am at least <strong>18 years old</strong>.
                </span>
              </label>
              {errors.ageEighteenPlus && (
                <p className="text-xs text-destructive" role="alert">
                  {errors.ageEighteenPlus.message as string}
                </p>
              )}

              <label className="flex items-start gap-3 text-sm">
                <Checkbox
                  id="privacyPolicyAccepted"
                  checked={!!privacyAccepted}
                  onCheckedChange={(v) =>
                    setValue(
                      'privacyPolicyAccepted',
                      v === true ? (true as true) : (false as unknown as true),
                      { shouldValidate: true },
                    )
                  }
                  aria-invalid={!!errors.privacyPolicyAccepted}
                />
                <span>
                  I have read and accept the{' '}
                  <Link to="/privacy" target="_blank" className="underline underline-offset-4">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
              {errors.privacyPolicyAccepted && (
                <p className="text-xs text-destructive" role="alert">
                  {errors.privacyPolicyAccepted.message as string}
                </p>
              )}

              <label className="flex items-start gap-3 text-sm">
                <Checkbox
                  id="termsAccepted"
                  checked={!!termsAcceptedValue}
                  onCheckedChange={(v) =>
                    setValue(
                      'termsAccepted',
                      v === true ? (true as true) : (false as unknown as true),
                      { shouldValidate: true },
                    )
                  }
                  aria-invalid={!!errors.termsAccepted}
                />
                <span>
                  I have read and accept the{' '}
                  <Link to="/terms" target="_blank" className="underline underline-offset-4">
                    Terms of Service
                  </Link>
                  .
                </span>
              </label>
              {errors.termsAccepted && (
                <p className="text-xs text-destructive" role="alert">
                  {errors.termsAccepted.message as string}
                </p>
              )}
            </div>
          </Field>

          {apiError && (
            <p className="text-sm text-destructive text-center" role="alert">
              {apiError}
            </p>
          )}
          <Field>
            <Button
              type="submit"
              disabled={mutation.isPending || !ageOk || !privacyAccepted || !termsAcceptedValue}
            >
              {mutation.isPending ? 'Activating…' : 'Activate Account →'}
            </Button>
          </Field>
          <p className="text-center text-xs text-[var(--text-secondary)]">
            Next: Set up two-factor authentication
          </p>
          <p className="text-center text-sm text-[var(--text-secondary)]">
            <Link
              to="/login"
              className="underline underline-offset-4 hover:text-[var(--text-accent)]"
            >
              Back to sign in
            </Link>
          </p>
        </FieldGroup>
      </form>
    </AuthShell>
  );
}
